/**
 * Fetch firmware releases for admin management.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { promoteFirmwareRelease } from '@/services/gateway';
import type { GatewayFirmwareRelease } from '@/types/gateway';

export function useGatewayFirmwareReleases() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['gateway-firmware-releases'],
    queryFn: async (): Promise<GatewayFirmwareRelease[]> => {
      const { data, error } = await supabase
        .from('gateway_firmware_releases')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as GatewayFirmwareRelease[];
    },
    staleTime: 60_000,
  });

  const promoteMutation = useMutation({
    mutationFn: async ({
      releaseId,
      nextStatus,
    }: {
      releaseId: string;
      nextStatus: string;
    }) => {
      return promoteFirmwareRelease(releaseId, nextStatus);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway-firmware-releases'] });
    },
  });

  return {
    releases: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
    promoteRelease: promoteMutation.mutateAsync,
    isPromoting: promoteMutation.isPending,
    promoteError: promoteMutation.error as Error | null,
  };
}
